import React, { useEffect, useState } from 'react';
import { Search } from 'lucide-react';
import { coaApi } from '../api/coa';

interface Account {
  id: number; account_code: string; account_name: string;
  type_name: string; subtype_name: string; normal_balance: string;
  requires_contact: number; is_system: number; is_active: number;
}

const typeOrder = ['Asset','Liability','Equity','Revenue','Expense'];

const badgeClass: Record<string, string> = {
  Asset: 'badge-blue', Liability: 'badge-red',
  Equity: 'badge-purple', Revenue: 'badge-green', Expense: 'badge-amber',
};

const ChartOfAccounts: React.FC = () => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [filtered, setFiltered] = useState<Account[]>([]);
  const [search, setSearch]     = useState('');
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    coaApi.list().then(res => {
      setAccounts(res.data.data);
      setFiltered(res.data.data);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    const q = search.toLowerCase();
    setFiltered(
      accounts.filter(a =>
        a.account_code.toLowerCase().includes(q) ||
        a.account_name.toLowerCase().includes(q) ||
        a.type_name.toLowerCase().includes(q)
      )
    );
  }, [search, accounts]);

  const grouped = typeOrder.reduce((acc, type) => {
    acc[type] = filtered.filter(a => a.type_name === type);
    return acc;
  }, {} as Record<string, Account[]>);

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Chart of Accounts</h1>
        <button className="btn btn-primary">+ Add Account</button>
      </div>

      <div className="filter-bar">
        <div className="search-box">
          <Search size={13} color="var(--tx3)" />
          <input
            placeholder="Search accounts..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <span className="record-count">{filtered.length} accounts</span>
      </div>

      {loading ? (
        <div className="empty-state"><p>Loading...</p></div>
      ) : (
        typeOrder.map(type => grouped[type]?.length > 0 && (
          <div className="card" key={type} style={{ marginBottom: 12 }}>
            <div className="card-header">
              <span>{type}</span>
              <span className="record-count">{grouped[type].length} accounts</span>
            </div>
            <table>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Account Name</th>
                  <th>Subtype</th>
                  <th>Normal Balance</th>
                  <th>Type</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {grouped[type].map(account => (
                  <tr key={account.id}>
                    <td className="td-code">{account.account_code}</td>
                    <td>{account.account_name}</td>
                    <td style={{ color: 'var(--tx3)' }}>{account.subtype_name}</td>
                    <td><span className={`badge ${account.normal_balance === 'debit' ? 'badge-blue' : 'badge-green'}`}>{account.normal_balance}</span></td>
                    <td><span className={`badge ${badgeClass[account.type_name]}`}>{account.type_name}</span></td>
                    <td><span className={`badge ${account.is_active ? 'badge-green' : 'badge-gray'}`}>{account.is_active ? 'Active' : 'Inactive'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))
      )}
    </>
  );
};

export default ChartOfAccounts;
